/**
 * Members seed — workspace "Avance Financial" (domain avance.com), 50 people.
 *
 * Baseline (prototype screen 01): 44 active, 4 pending invitations (2 invited,
 * 1 expired, 1 not sent), 8 active without MFA, plus 1 suspended and 1 removed.
 * 1 Owner, 2 Admins, 47 Members. Timestamps are relative to "now" so
 * “3d ago” stays true whenever the mock is loaded.
 */

export const WORKSPACE = { id: 'ws-avance', name: 'Avance Financial', domain: 'avance.com' };
export const INVITATION_TTL_DAYS = 7;

const DAY = 86_400_000;
const daysAgo = (n) => new Date(Date.now() - n * DAY).toISOString();

const slug = (s) =>
  s
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '.')
    .replace(/^\.|\.$/g, '');

const R = {
  dev: 'role-developer',
  viewer: 'role-viewer',
  builder: 'role-agent-builder',
  steward: 'role-data-steward',
};
const G = {
  eng: 'grp-engineering',
  ai: 'grp-ai-ops',
  data: 'grp-data-team',
  lead: 'grp-leadership',
};

/**
 * [first, last, job_title, department, user_type, status, mfa, last_active_days_ago,
 *  added_days_ago, role_ids, group_ids, email?]
 */
const NAMED = [
  ['Tatiana', 'Rojas', 'Workspace Owner', 'Executive', 'owner', 'active', true, 0, 520, [], []],
  [
    'Teresa',
    'Quintero',
    'Chief Operating Officer',
    'Executive',
    'admin',
    'active',
    true,
    1,
    500,
    [],
    [G.lead],
  ],
  ['Rafael', 'Gómez', 'Head of Risk', 'Risk', 'admin', 'active', true, 2, 480, [], [G.lead]],
  ['Ana Lucía', 'Paredes', 'Credit Analyst', 'Credit', 'member', 'active', true, 21, 270, [], []],
  [
    'Ana',
    'Torres',
    'Business Analyst',
    'Analytics',
    'member',
    'active',
    false,
    3,
    439,
    [R.viewer],
    [],
  ],
  ['Andrés', 'Morales', 'Product Manager', 'Product', 'member', 'active', true, 6, 360, [], []],
  ['Camila', 'Ortiz', 'Financial Planner', 'Finance', 'member', 'active', true, 7, 330, [], []],
  ['Carlos', 'Rivera', 'Treasury Analyst', 'Finance', 'member', 'active', true, 1, 540, [], []],
  ['Chloe', 'Adams', 'Customer Success Lead', 'Support', 'member', 'active', true, 4, 210, [], []],
  [
    'Clara',
    'Nakamura',
    'Compliance Associate',
    'Compliance',
    'member',
    'invited',
    false,
    null,
    2,
    [],
    [],
  ],
  ['Leo', 'Ramírez', 'Data Engineer', 'Analytics', 'member', 'expired', false, null, 16, [], []],
  [
    null,
    null,
    null,
    null,
    'member',
    'not_invited',
    false,
    null,
    1,
    [],
    [],
    'natalia.kim@avance.com',
  ],
  [
    'Eduardo',
    'Suárez',
    'Data Analyst',
    'Analytics',
    'member',
    'active',
    true,
    0.67,
    400,
    [R.dev],
    [G.eng, G.lead],
  ],
  [
    'Diana',
    'Pérez',
    'Operations Lead',
    'Operations',
    'member',
    'active',
    false,
    1,
    420,
    [R.dev, R.builder],
    [G.eng, G.ai],
  ],
  [
    'Mateo',
    'Silva',
    'Data Steward',
    'Data',
    'member',
    'active',
    true,
    2,
    300,
    [R.steward],
    [G.data],
  ],
  [
    'Sebastián',
    'Blanco',
    'ML Engineer',
    'AI',
    'member',
    'active',
    true,
    1,
    310,
    [R.dev, R.builder],
    [G.ai],
  ],
  [
    'Wendy',
    'Ruiz',
    'Audit Associate',
    'Compliance',
    'member',
    'active',
    true,
    5,
    250,
    [R.viewer],
    [],
  ],
  [
    'Tomás',
    'Beltrán',
    'Data Governance Analyst',
    'Data',
    'member',
    'active',
    true,
    9,
    280,
    [R.steward],
    [],
  ],
  [
    'Pablo',
    'Ferrer',
    'Software Engineer',
    'Engineering',
    'member',
    'invited',
    false,
    null,
    4,
    [],
    [],
  ],
  ['Julián', 'Herrera', 'Risk Analyst', 'Risk', 'member', 'suspended', true, 45, 380, [], []],
  [
    'Gabriela',
    'Núñez',
    'Operations Analyst',
    'Operations',
    'member',
    'removed',
    false,
    60,
    450,
    [],
    [],
  ],
];

const FILLER_NAMES = [
  ['Valentina', 'Cruz'],
  ['Hugo', 'Mendoza'],
  ['Isabella', 'Reyes'],
  ['Martín', 'Castillo'],
  ['Lucía', 'Fernández'],
  ['Santiago', 'Vargas'],
  ['Paula', 'Ibarra'],
  ['Daniel', 'Salazar'],
  ['Elena', 'Campos'],
  ['Nicolás', 'Aguirre'],
  ['Sofía', 'Lozano'],
  ['Gonzalo', 'Peña'],
  ['Marina', 'Duarte'],
  ['Xavier', 'Cortés'],
  ['Renata', 'Molina'],
  ['Felipe', 'Acosta'],
  ['Natalia', 'Soto'],
  ['Iván', 'Ramos'],
  ['Julia', 'Escobar'],
  ['Ulises', 'Cabrera'],
  ['Olivia', 'Park'],
  ['Noah', 'Brooks'],
  ['Emma', 'Walsh'],
  ['Liam', 'Foster'],
  ['Mia', 'Tanaka'],
  ['Ethan', 'Cole'],
  ['Grace', 'Holt'],
  ['Lucas', 'Meyer'],
  ['Rocío', 'Medina'],
];
const FILLER_ROLES = [
  ['Credit Analyst', 'Credit'],
  ['Software Engineer', 'Engineering'],
  ['Support Specialist', 'Support'],
  ['Accountant', 'Finance'],
  ['Risk Analyst', 'Risk'],
  ['Product Designer', 'Product'],
  ['Operations Analyst', 'Operations'],
];
// Filler indexes without MFA: with Ana Torres and Diana Pérez that makes 8 active without MFA.
const FILLER_NO_MFA = new Set([2, 6, 11, 15, 19, 23]);

const toMember = (row, index) => {
  const [
    first,
    last,
    job,
    dept,
    user_type,
    status,
    mfa,
    lastActive,
    added,
    role_ids,
    group_ids,
    email,
  ] = row;
  const name = first ? `${first} ${last}` : '';
  const added_at = daysAgo(added);
  const pending = status === 'invited' || status === 'expired' || status === 'not_invited';
  return {
    id: `mbr-${String(index + 1).padStart(3, '0')}`,
    first_name: first,
    last_name: last,
    name,
    email: email ?? `${slug(name)}@${WORKSPACE.domain}`,
    job_title: job,
    department: dept,
    user_type,
    status,
    mfa_enabled: mfa,
    last_active_at: lastActive == null ? null : daysAgo(lastActive),
    added_at,
    invited_at:
      status === 'not_invited' || !pending ? null : daysAgo(status === 'expired' ? 16 : added),
    invitation_expires_at:
      status === 'invited'
        ? new Date(Date.now() + (INVITATION_TTL_DAYS - added) * DAY).toISOString()
        : status === 'expired'
          ? daysAgo(9)
          : null,
    suspended_at: status === 'suspended' ? daysAgo(30) : null,
    removed_at: status === 'removed' ? daysAgo(20) : null,
    role_ids: [...role_ids],
    group_ids: [...group_ids],
    import_batch_id: null,
  };
};

export const buildSeedMembers = () => {
  const fillers = FILLER_NAMES.map(([first, last], i) => {
    const [job, dept] = FILLER_ROLES[i % FILLER_ROLES.length];
    return [
      first,
      last,
      job,
      dept,
      'member',
      'active',
      !FILLER_NO_MFA.has(i),
      (i * 5) % 28,
      20 + ((i * 37) % 480),
      [],
      [],
    ];
  });
  return [...NAMED, ...fillers].map(toMember);
};
